/* ==========================================================================
   Satjeevan — "The Bilona Method" V2 Section JS (bilona-method-v2.js)

   Drives the filmstrip: auto-advances through the process-step blocks on
   its own timer once the section scrolls into view (Enable/Speed are
   merchant settings — see data-autoplay / data-autoplay-ms below).
   Hovering, clicking, or focusing a stage always wins — it pauses
   autoplay, highlights just that stage, and swaps the caption; autoplay
   resumes afterwards, picking up from where it left off.

   Independent from assets/sj-bilona-method.js — same proven interaction
   pattern (this is what the reference itself does, in both design
   references), reused rather than reinvented, but scoped to this
   section's own class names so the two sections never collide if both
   are present on a page at once.

   Scroll-aware autoplay: the periodic tick is paused the instant any
   scrolling happens and only resumes ~200ms after scrolling settles. A
   bare setInterval has no awareness of an in-progress scroll gesture, so
   without this its tick can land mid-scroll on mobile — competing with
   the browser's own scroll/compositing work for the main thread and
   showing up as a stutter, worse the longer autoplay has been running.
   Nothing here fires while the page is actively being scrolled.

   No dependencies — vanilla JS only, no globals.
   ========================================================================== */

(function () {
  'use strict';

  var INIT_ATTR = 'data-sj-bmv2-init';

  function initFilmstrip() {
    var wraps = document.querySelectorAll('[data-sj-bmv2-wrap]:not([' + INIT_ATTR + '])');
    if (!wraps.length) return;

    wraps.forEach(function (wrap) {
      wrap.setAttribute(INIT_ATTR, '1');

      var fill = wrap.querySelector('[data-sj-bmv2-fill]');
      var stages = Array.prototype.slice.call(wrap.querySelectorAll('[data-sj-bmv2-stage]'));
      var captionId = wrap.getAttribute('data-caption-id');
      var caption = captionId ? document.getElementById(captionId) : null;
      if (!stages.length || !caption) return;

      var autoplayEnabled = caption.getAttribute('data-autoplay') === 'true';
      var autoplayMs = parseInt(caption.getAttribute('data-autoplay-ms'), 10) || 2600;
      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      var current = 0;
      var timer = null;
      var inView = false;
      var scrollResumeTimer = null;

      function captionFor(index) {
        var stage = stages[index];
        return stage ? stage.getAttribute('data-caption') : '';
      }

      function setActive(i) {
        current = i;
        stages.forEach(function (s, j) {
          s.classList.toggle('active', j === i);
        });
        if (fill) {
          fill.style.width = (((i + 1) / stages.length) * 100) + '%';
        }
        caption.textContent = captionFor(i);
      }

      function step() {
        setActive((current + 1) % stages.length);
      }

      function startAutoplay() {
        if (timer || !inView || !autoplayEnabled || reducedMotion) return;
        timer = setInterval(step, autoplayMs);
      }

      function stopAutoplay() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }

      // Pause the instant any scrolling happens, resume shortly after it
      // settles — see the file header for why. Passive listener: it only
      // ever clears/sets timers, never touches layout, so it can't itself
      // add scroll overhead.
      function onWindowScroll() {
        stopAutoplay();
        if (scrollResumeTimer) clearTimeout(scrollResumeTimer);
        scrollResumeTimer = setTimeout(function () {
          scrollResumeTimer = null;
          startAutoplay();
        }, 200);
      }

      stages.forEach(function (s, i) {
        s.addEventListener('mouseenter', function () {
          stopAutoplay();
          setActive(i);
        });
        s.addEventListener('mouseleave', function () {
          startAutoplay();
        });
        s.addEventListener('click', function () {
          stopAutoplay();
          setActive(i);
          startAutoplay();
        });
        // Keyboard accessibility — tabbing through stages behaves the same as hovering
        s.addEventListener('focus', function () {
          stopAutoplay();
          setActive(i);
        });
        s.addEventListener('blur', function () {
          startAutoplay();
        });
      });

      setActive(0);

      if (!autoplayEnabled || reducedMotion) return;

      window.addEventListener('scroll', onWindowScroll, { passive: true });

      if (!('IntersectionObserver' in window)) {
        inView = true;
        startAutoplay();
        return;
      }

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting;
          if (inView) {
            startAutoplay();
          } else {
            stopAutoplay();
          }
        });
      }, { threshold: 0.4 });

      observer.observe(wrap);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilmstrip);
  } else {
    initFilmstrip();
  }

  // Shopify Theme Editor live-reload support
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('[data-sj-bmv2-wrap]')) {
      initFilmstrip();
    }
  });
})();
